import { useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface SkinColorItem {
  id: string;
  label: string;
}

interface SkinColorProps {
  options?: SkinColorItem[];
  onContinue: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

const defaultSkinTypeOptions: SkinColorItem[] = [
  { id: "very-fair", label: "Very fair" },
  { id: "fair-light", label: "Fair/Light" },
  { id: "medium-olive", label: "Medium/Olive" },
  { id: "tan-light-brown", label: "Tan/Light brown" },
  { id: "brown", label: "Brown" },
  { id: "deep-brown-black", label: "Deep brown to black" },
];

export const SkinColor = ({
  options = defaultSkinTypeOptions,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText = "Continue",
  skipText = "Skip for now",
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: SkinColorProps) => {
  const [selected, setSelected] = useState<SkinColorItem | null>(
    initialSelectedId
      ? options.find((o) => o.id === initialSelectedId) ?? null
      : null
  );

  const handleSelectionChange = (item: SkinColorItem | null) => {
    setSelected(item);
    onContinue?.(item?.id ?? "");
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const isContinueDisabled = selected === null;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={isContinueDisabled}
            onClick={handleContinue}
          >
            {continueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {skipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">
        Which of these best describes your skin tone?
      </PersonalisationHeading>

      <SingleChoiceTagPicker
        items={options}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel="Select your skin type"
      />
    </PersonalisationScreenLayout>
  );
};
